/**
 * fillDay (PLANNER-02 A) — the pure math behind "Fill this day".
 *
 * A tech-day is the batch. Given what's already on the tech's Board (fixed
 * calls with promised windows) and the still-owed members nearby, this
 * module (1) picks which members to propose and (2) lays the whole day out
 * in drive order, giving every proposed stop an estimated start hour and
 * the arrival window that hour falls in. Fixed calls never move: a
 * "Burnsville by noon" already promised is a deadline the route bends
 * around.
 *
 * Numbers: a check takes about an hour door to door (CHECK_HOURS), driving
 * is haversine × road factor at 28 mph (lib/route), the day runs 8 → 17.
 */
import { haversineMiles, driveMinutes, type LatLng } from "@/lib/route";

export const CHECK_HOURS = 1.0;
export const DAY_START = 8;
export const DAY_END = 17;

/** Sedgwick's arrival windows → [start, end] hours. Unknown codes are flexible all day. */
export function windowHours(code: string | null | undefined): [number, number] {
  const w = (code ?? "").toUpperCase().trim();
  if (w === "8AM" || w === "8 AM") return [8, 9];
  if (w === "7-8") return [7, 8];
  if (w === "8-12") return [8, 12];
  if (w === "10-2") return [10, 14];
  if (w === "11-1") return [11, 13];
  if (w === "12-4") return [12, 16];
  if (w === "3-7") return [15, 19];
  if (w.includes("AFTER")) return [16, 19];
  const m = /^(\d{1,2})-(\d{1,2})$/.exec(w);
  if (m) {
    let a = parseInt(m[1], 10); let b = parseInt(m[2], 10);
    if (a >= 1 && a <= 6) a += 12;
    if (b >= 1 && b <= 7) b += 12;
    if (b > a) return [a, b];
  }
  return [DAY_START, DAY_END];
}

const KNOWN_WINDOWS = new Set(["8AM", "8 AM", "7-8", "8-12", "10-2", "11-1", "12-4", "3-7"]);

/**
 * ESC's window_code is sometimes a flag, not a window — PAIR, LONG, TECH,
 * "------" — and the real promise sits at the front of the notes
 * ("8-12, 3 of 4, PLUS AC CK…", "8am start, …"). Read it from there.
 */
export function effectiveWindow(code: string | null | undefined, notes: string | null | undefined): string | null {
  const w = (code ?? "").toUpperCase().trim();
  if (KNOWN_WINDOWS.has(w) || w.includes("AFTER")) return w === "8 AM" ? "8AM" : w;
  const n = (notes ?? "").trim();
  const m = /^(8\s?am|7-8|8-12|10-2|11-1|12-4|3-7)\b/i.exec(n);
  if (m) { const t = m[1].toUpperCase().replace(/\s+/g, ""); return t === "8AM" ? "8AM" : t; }
  return w || null;
}

/** The window a proposed stop gets from its estimated start hour. */
export function windowForHour(h: number): string {
  if (h < 10.5) return "8-12";
  if (h < 12.5) return "10-2";
  if (h < 14.75) return "12-4";
  return "3-7";
}

export function fmtHour(h: number): string {
  const hh = Math.floor(h); const mm = Math.round((h - hh) * 60);
  const ap = hh >= 12 ? "PM" : "AM";
  const h12 = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
  return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

export interface FixedCall extends LatLng {
  id: string;
  window: string | null;
  /** Time on site (default CHECK_HOURS); a part pickup is ~15 minutes. */
  hours?: number;
}
export interface FreeStop extends LatLng {
  id: string;
}
export interface SequencedStop {
  id: string;
  kind: "fixed" | "free";
  eta: number;           // estimated start hour
  window: string;        // fixed: its own code; free: from the eta
  late: boolean;         // a fixed call the route can't reach inside its window
  milesFromPrev: number;
}

function driveHours(a: LatLng, b: LatLng): number {
  return driveMinutes(haversineMiles(a, b)) / 60;
}

/**
 * Lay a day out. Fixed calls keep their windows and are visited in window
 * order; free stops are threaded between them nearest-first, never in a way
 * that makes the next fixed call late. Returns the sequence with ETAs.
 */
export function sequenceDay(fixed: FixedCall[], free: FreeStop[], start?: LatLng | null, startHour: number = DAY_START): SequencedStop[] {
  const fq = [...fixed].sort((a, b) => {
    const [as, ae] = windowHours(a.window); const [bs, be] = windowHours(b.window);
    return as - bs || ae - be;
  });
  const pool = [...free];
  const out: SequencedStop[] = [];
  let t = startHour; // 8:15 when the day opens with a part pickup at the office
  let cur: LatLng | null = start ?? (fq[0] ?? pool[0] ?? null);
  const dh = (a: LatLng | null, b: LatLng) => (a ? driveHours(a, b) : 0);
  const dm = (a: LatLng | null, b: LatLng) => (a ? haversineMiles(a, b) : 0);

  // Every remaining promised call still starts inside its window if we leave
  // `from` at hour `t0` and run them in order.
  const stillOnTime = (t0: number, from: LatLng | null, queue: FixedCall[]): boolean => {
    let tt = t0; let at = from;
    for (const f of queue) {
      const [ws, we] = windowHours(f.window);
      const arrive = Math.max(tt + dh(at, f), ws);
      const dwell = f.hours ?? CHECK_HOURS;
      if (arrive + dwell > we + 0.01) return false;
      tt = arrive + dwell; at = f;
    }
    return true;
  };

  while (fq.length > 0 || pool.length > 0) {
    const nf = fq[0] ?? null;
    if (nf) {
      const [ws, we] = windowHours(nf.window);
      const dwell = nf.hours ?? CHECK_HOURS;
      const arriveNow = Math.max(t + dh(cur, nf), ws);
      // Deadline: the job has to START inside the window (or ASAP if already late).
      const mustLeave = arriveNow + dwell > we || pool.length === 0;
      if (mustLeave) {
        out.push({ id: nf.id, kind: "fixed", eta: arriveNow, window: nf.window ?? windowForHour(arriveNow), late: t + dh(cur, nf) > we, milesFromPrev: dm(cur, nf) });
        t = arriveNow + dwell; cur = nf; fq.shift();
        continue;
      }
      // Can we squeeze a free stop in before it — without making ANY promised call late?
      let best = -1; let bestD = Infinity;
      for (let i = 0; i < pool.length; i++) {
        const c = pool[i];
        const arriveC = t + dh(cur, c);
        if (!stillOnTime(arriveC + CHECK_HOURS, c, fq)) continue;
        const d = dm(cur, c);
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best < 0) {
        out.push({ id: nf.id, kind: "fixed", eta: arriveNow, window: nf.window ?? windowForHour(arriveNow), late: t + dh(cur, nf) > we, milesFromPrev: dm(cur, nf) });
        t = arriveNow + dwell; cur = nf; fq.shift();
        continue;
      }
      const c = pool.splice(best, 1)[0];
      const eta = t + dh(cur, c);
      out.push({ id: c.id, kind: "free", eta, window: windowForHour(eta), late: false, milesFromPrev: dm(cur, c) });
      t = eta + CHECK_HOURS; cur = c;
      continue;
    }
    // No fixed calls left: nearest neighbour through the pool.
    let best = 0; let bestD = Infinity;
    for (let i = 0; i < pool.length; i++) {
      const d = dm(cur, pool[i]);
      if (d < bestD) { bestD = d; best = i; }
    }
    const c = pool.splice(best, 1)[0];
    const eta = t + dh(cur, c);
    out.push({ id: c.id, kind: "free", eta, window: windowForHour(eta), late: false, milesFromPrev: dm(cur, c) });
    t = eta + CHECK_HOURS; cur = c;
  }
  return out;
}

export interface Candidate extends LatLng {
  key: string;
  pickedForDate: boolean;
  renewal: boolean;
  atRisk: boolean;
  invited: boolean;
}

/**
 * Which members to propose for a tech-day: everyone who asked for that date
 * first, then the nearest still-owed checks to the anchor with a nudge toward
 * renewals and at-risk plans. Never farther than `maxMiles` from the anchor.
 */
export function pickCandidates<T extends Candidate>(anchor: LatLng | null, pool: T[], room: number, maxMiles: number): T[] {
  if (room <= 0) return [];
  const picked = pool.filter((p) => p.pickedForDate);
  const rest = pool.filter((p) => !p.pickedForDate);
  const a = anchor ?? (picked.length > 0 ? { lat: picked.reduce((s, p) => s + p.lat, 0) / picked.length, lng: picked.reduce((s, p) => s + p.lng, 0) / picked.length } : null);
  const scored = rest
    .map((p) => {
      const d = a ? haversineMiles(a, p) : 0;
      const bonus = (p.renewal ? 1.5 : 0) + (p.atRisk ? 1.5 : 0) + (p.invited ? 0.5 : 0);
      return { p, d, score: d - bonus };
    })
    .filter((x) => x.d <= maxMiles)
    .sort((x, y) => x.score - y.score);
  const out: T[] = [...picked.slice(0, room)];
  for (const x of scored) {
    if (out.length >= room) break;
    out.push(x.p);
  }
  return out;
}

/** Point in polygon (ray casting) for Direction B's loop. Polygon = [[lat, lng], …]. */
export function insideLoop(p: LatLng, loop: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const [yi, xi] = loop[i]; const [yj, xj] = loop[j];
    const hit = (yi > p.lat) !== (yj > p.lat) && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}
